import { useEffect, useMemo } from 'react';

// ═══════════════════════════════════════════════════════════════════════════════
// useTypewriterPlaceholder
//
// Types example prompts into a set of pre-rendered spans by writing textContent
// directly. No React state, therefore no renders — driving this through state
// would re-render the whole chat page ~20 times a second.
//
// Each example is an array of segments: [{ text, kind }]. `kind: 'file' | 'task'`
// marks a segment that should render as a reference chip. That is exactly why
// this cannot be a CSS `content: attr(...)` placeholder — a pseudo-element is a
// single anonymous inline box and cannot contain differently-styled runs.
// ═══════════════════════════════════════════════════════════════════════════════

const TYPE_MS = 45;
const DELETE_MS = 22;
const HOLD_MS = 1500;
const GAP_MS = 320;

export function useTypewriterPlaceholder({ rootRef, examples, enabled }) {
    // Examples are rebuilt whenever files/tasks change identity; key on content
    // so the animation does not restart on every unrelated parent render.
    const key = useMemo(() => JSON.stringify(examples), [examples]);

    useEffect(() => {
        const root = rootRef.current;
        if (!enabled || !root || !examples?.length) return undefined;

        const spans = Array.from(root.querySelectorAll('[data-seg]'));
        if (!spans.length) return undefined;

        let exampleIndex = 0;
        let count = 0;
        let deleting = false;
        let timer = null;
        let cancelled = false;

        const totalLength = (ex) => ex.reduce((sum, seg) => sum + seg.text.length, 0);

        const paint = (ex, n) => {
            let remaining = n;
            spans.forEach((span, i) => {
                const seg = ex[i];
                if (!seg) {
                    span.textContent = '';
                    span.style.display = 'none';
                    return;
                }
                const take = Math.max(0, Math.min(seg.text.length, remaining));
                span.textContent = seg.text.slice(0, take);
                // Chip segments only materialise once a character of them exists,
                // otherwise an empty pill flashes ahead of the text.
                span.style.display = take === 0 ? 'none' : '';
                remaining -= seg.text.length;
            });
        };

        const step = () => {
            if (cancelled) return;
            const ex = examples[exampleIndex % examples.length];
            const total = totalLength(ex);

            paint(ex, count);

            let delay = deleting ? DELETE_MS : TYPE_MS;

            if (!deleting && count >= total) {
                deleting = true;
                delay = HOLD_MS;
            } else if (deleting && count <= 0) {
                deleting = false;
                exampleIndex += 1;
                delay = GAP_MS;
            } else {
                count += deleting ? -1 : 1;
            }

            timer = setTimeout(step, delay);
        };

        step();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            spans.forEach(span => {
                span.textContent = '';
                span.style.display = 'none';
            });
        };
    }, [enabled, key, examples, rootRef]);
}

export default useTypewriterPlaceholder;
